import { useEffect, useState } from "react";
import { Button, Grid, Typography, Divider } from "@material-ui/core";
import Api from "lib/api/httpRequestHelper";
import { IsMobile } from "utils/is-mobile";
import useSnackbar from "components/Alerts/Snackbar/useSnackbar";
import InputText from "components/_refactor/text/input";
import ChooseInvoiceDialog from "../components/choose-invoice-dialog";

function mapApiNotaToInvoiceObj(nota) {
  const idPedido = nota.IdPedido != null ? nota.IdPedido : null;
  const lista = nota.ListaProdutos || [];

  const listaProdutosExport = lista.map((item) => ({
    Codigo: item.CodigoProduto,
    Descricao: item.DescricaoProduto,
    QtdFaturada: item.QtdFaturada,
    QuantidadeDevolvida: item.QuantidadeDevolvida ?? 0,
    ValorUnitario: item.ValorUnitario ?? 0,
    NF: item.Numero,
    Serie: item.Serie,
  }));

  return {
    NumeroNf: nota.NumeroNf ?? null,
    ValorTotal: nota.ValorLiquidoNf ?? null,
    SerieNf: nota.SerieNf || null,
    DataEmissaoNf: nota.DataEmissaoNf ?? null,
    ChaveNf: nota.ChaveNf || null,
    IdPedido: idPedido,
    items: listaProdutosExport.map((item) => {
      const qf = Number(item.QtdFaturada) || 0;
      const vu = Number(item.ValorUnitario) || 0;
      const qd = Number(item.QuantidadeDevolvida) || 0;
      return {
        selected: false,
        idTableProduct: idPedido,
        product: item,
        name: item.Descricao || null,
        idProduto: item.Codigo || null,
        nameMotivo: undefined,
        nameDefeito: undefined,
        quantity: qf - qd || null,
        motivo: undefined,
        defeito: undefined,
        IdPedido: idPedido,
        QtdFaturada: item.QtdFaturada || null,
        QuantidadeDevolvida: qd,
        ValorUnitario: item.ValorUnitario || null,
        ValorTotal: (qf * vu).toFixed(2),
      };
    }),
  };
}

export default function InvoiceChoose({
  fieldsData,
  loadingFields,
  isLoading,
  setIsLoading,
  addedProductsList,
  setAddedProductsList,
  clearFilters,
  motivoRequerEvidencia,
}) {
  const [invoice, setInvoice] = useState("");
  const [invoiceSelected, setInvoiceSelected] = useState(null);
  const [isInvoiceValid, setIsInvoiceValid] = useState();
  const [isInvoiceAlreadySelected, setIsInvoiceAlreadySelected] =
    useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [searchingInvoice, setSearchingInvoice] = useState("");

  const snackbar = useSnackbar();
  const isMobile = IsMobile();

  const handleSearchInvoice = async () => {
    const invoiceValue = invoice.trim();

    if (!invoiceValue) {
      return;
    }

    if (fieldsData?.idPessoa == null || fieldsData.idPessoa === "") {
      snackbar.showMessage(
        "Selecione o grupo e a loja (Razão Social) antes de pesquisar a nota fiscal.",
        { severity: "warning" }
      );
      return;
    }

    if (
      addedProductsList.some(
        (product) => String(product.NumeroNf ?? "") === invoiceValue
      )
    ) {
      setIsInvoiceAlreadySelected(true);
      return;
    }

    setIsInvoiceAlreadySelected(false);
    setIsInvoiceValid(undefined);
    setSearchingInvoice(invoiceValue);
    setIsLoading(true);

    try {
      // API Gateway exige situacao e ordenacao. numeroNf: rota completa do Lambda filtra por numeroNf (idNF sozinho era ignorado).
      const qs = new URLSearchParams({
        simples: "true",
        idLoja: String(fieldsData.idPessoa),
        idNF: invoiceValue,
        numeroNf: invoiceValue,
        situacao: "todos",
        ordenacao: "4",
      });
      const response = await Api.get(
        "list-reception-invoices",
        "invoice",
        `?${qs.toString()}`,
        true
      );

      const validation =
        response?.ValidationMessage || response?.validationMessage;
      if (validation) {
        snackbar.showMessage(String(validation), { severity: "error" });
        setIsInvoiceValid(false);
        setSearchingInvoice("");
        return;
      }

      if (response?.Success === false) {
        const msg =
          response?.Message ||
          response?.message ||
          "Não foi possível consultar a nota fiscal.";
        snackbar.showMessage(String(msg), { severity: "error" });
        setIsInvoiceValid(false);
        setSearchingInvoice("");
        return;
      }

      const listaNotas = response?.Result?.ListaNotas;
      if (!listaNotas || listaNotas.length === 0) {
        setIsInvoiceValid(false);
        snackbar.showMessage(
          "Nenhuma nota fiscal encontrada para a loja e o número informados.",
          { severity: "info" }
        );
        setSearchingInvoice("");
        return;
      }

      const nota = listaNotas[0];
      const produtos = nota.ListaProdutos || [];

      if (produtos.length === 0) {
        setIsInvoiceValid(false);
        snackbar.showMessage("Nota fiscal sem produtos disponíveis", {
          severity: "error",
        });
        setSearchingInvoice("");
        return;
      }

      const invoiceObj = mapApiNotaToInvoiceObj(nota);
      const invoiceNumberToRestore =
        searchingInvoice || String(nota.NumeroNf ?? "") || invoice;

      setInvoiceSelected(invoiceObj);
      setIsInvoiceValid(true);
      setOpenDialog(true);

      if (!invoice && invoiceNumberToRestore) {
        setInvoice(String(invoiceNumberToRestore));
      }

      setSearchingInvoice("");
    } catch (error) {
      const msg =
        typeof error === "string"
          ? error
          : error?.message || "Erro ao pesquisar a nota fiscal.";
      snackbar.showMessage(msg, { severity: "error" });
      setIsInvoiceValid(false);
      setSearchingInvoice("");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (clearFilters && !openDialog) {
      setInvoice("");
      setInvoiceSelected(null);
      setIsInvoiceValid(undefined);
      setIsInvoiceAlreadySelected(false);
      setOpenDialog(false);
      setSearchingInvoice("");
    }
  }, [clearFilters, openDialog]);

  useEffect(() => {
    if (searchingInvoice && !invoice && !isLoading) {
      setInvoice(searchingInvoice);
    }
  }, [searchingInvoice, invoice, isLoading]);

  return (
    <>
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <InputText
            label="Nota Fiscal"
            disabled={loadingFields.length > 0}
            value={invoice}
            onChange={(e) => {
              const value = e.target.value
                .toString()
                .replace(/\D/g, "")
                .slice(0, 10);
              setInvoice(value);
              setIsInvoiceValid(undefined);
              setIsInvoiceAlreadySelected(false);
            }}
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <Button
            style={{
              width: isMobile ? "100%" : "17vw",
              marginBottom: isMobile ? "0.75rem" : "0px",
              marginTop: isMobile ? "0.75rem" : "20px",
              height: "38px",
              alignSelf: "center",
              padding: "0",
            }}
            variant="contained"
            onClick={handleSearchInvoice}
            disabled={!invoice || loadingFields.length > 0 || isLoading}
          >
            {isLoading ? "Pesquisando..." : "Pesquisar"}
          </Button>
        </Grid>

        {isInvoiceAlreadySelected && (
          <Typography
            variant="h5"
            component="h5"
            style={{ color: "grey", marginTop: "1rem" }}
          >
            <i
              className="fa fa-exclamation-circle"
              aria-hidden="true"
              style={{
                fontSize: "1rem",
                marginRight: "0.5rem",
              }}
            />{" "}
            Nota fiscal já selecionada.
          </Typography>
        )}

        {isInvoiceValid === false && (
          <Typography
            variant="h5"
            component="h5"
            style={{ color: "grey", marginTop: "1rem" }}
          >
            <i
              className="fa fa-exclamation-circle"
              aria-hidden="true"
              style={{
                fontSize: "1rem",
                marginRight: "0.5rem",
              }}
            />{" "}
            Nota informado não encontrada.
          </Typography>
        )}
      </Grid>

      <Divider variant="fullWidth" style={{ margin: "20px 0px" }} />

      {openDialog && invoiceSelected && (
        <ChooseInvoiceDialog
          onClose={() => {
            setOpenDialog(false);
            setInvoice("");
            setInvoiceSelected(null);
          }}
          invoice={invoiceSelected}
          selectedProductsList={addedProductsList}
          setSelectedProductsList={setAddedProductsList}
          motivoRequerEvidencia={motivoRequerEvidencia}
        />
      )}
    </>
  );
}
